import { rankOf } from '../core/cards';
import { comboIndex } from '../range/combos';
import { parseRange } from '../range/parse';
import { startingRange } from '../range/starting';
// PF5 push/fold routing. Imported by SPECIFIC FILE (not the `../tournament` barrel) on purpose: these
// two modules depend only on schema + range/ (verified), so strategy → tournament/pushfold introduces NO
// import cycle, whereas the barrel would pull in tournament/recommend.ts → strategy (a real cycle). The
// depth threshold itself is OWNED by tournament/staging (isPushFoldDepth / PUSH_FOLD_MAX_BB, anchored to
// the DT chart domain); this module consumes it and never redefines a routing number.
import { resolveJam, resolveCallOff, pushFoldPositionOf, shoverClassOf } from '../tournament/pushfold';
import { isPushFoldDepth } from '../tournament/staging';
import { ruleMatches } from './match';
import { collectEffects, orderRules, toFiredRule } from './resolve';
import { positionBucket } from './villainRange';
/** The width order of the three table-type families (tightest → loosest) for OPEN/DEFEND widen shifts.
 *  Note: this ordering holds for RFI + `_call` charts (looser = wider); it deliberately does NOT govern
 *  3bet charts (where looser = value-only tighter), which is why 3bet widen/tighten are handled at the
 *  action level, not by a family swap. */
const WIDTH_ORDER = ['live_tight', 'baseline', 'live_loose'];
const shiftTableType = (t, dir) => {
    const i = WIDTH_ORDER.indexOf(t);
    const j = dir === 'looser' ? Math.min(i + 1, WIDTH_ORDER.length - 1) : Math.max(i - 1, 0);
    return WIDTH_ORDER[j];
};
/** 100bb cash stack-off vs a 4-bet or a preflop jam (no authored chart for these rare nodes). AKo and
 *  JJ are close folds at 100bb by default; short-stack widens this, which is PF5's Nash/ICM path. */
const PREMIUM_STACKOFF = parseRange('QQ+, AKs');
const EPS = 1e-9;
const familyId = (t, size) => `${t}_${size}max`;
// ── chart membership ────────────────────────────────────────────────────────────────────────────
const inRange = (r, cards) => r.weights[comboIndex(cards[0], cards[1])] > EPS;
/** A pocket pair (both hole cards the same rank). Value-widening a defend promotes flats → 3bets, but
 *  a small/medium pocket pair prefers to keep flatting for set value, so it is exempt from the promote. */
const isPocketPair = (cards) => rankOf(cards[0]) === rankOf(cards[1]);
/** The parsed WeightedRange for (family, spotKey), or undefined if the family isn't supplied or the key
 *  isn't in that family (early-vs-early has no `_vs_` chart, e.g.). No card removal: this checks HERO's
 *  OWN combo membership, so hero's cards must NOT be removed (unlike a villain-range resolve). */
const chartFor = (ctx, t, spotKey) => {
    const set = ctx.families[familyId(t, ctx.tableSize)];
    if (!set || set.ranges[spotKey] === undefined)
        return undefined;
    return startingRange(set, spotKey, {});
};
const fold = (role, familyIdStr) => ({
    role,
    intent: 'fold',
    family: familyIdStr,
    chartKey: 'none',
    inChart: false,
});
const resolveOpen = (ctx, t) => {
    const key = `${ctx.hero.position}_RFI`;
    const chart = chartFor(ctx, t, key);
    const hit = chart !== undefined && inRange(chart, ctx.hero.cards);
    const fam = familyId(t, ctx.tableSize);
    return hit
        ? { role: 'open', intent: 'open', family: fam, chartKey: key, inChart: true }
        : fold('open', fam);
};
const resolveDefend = (ctx, t) => {
    const fam = familyId(t, ctx.tableSize);
    const b = ctx.facing.openerPosition ? positionBucket(ctx.facing.openerPosition) : 'BTN';
    const key3 = `${ctx.hero.position}_vs_${b}_3bet`;
    const keyC = `${ctx.hero.position}_vs_${b}_call`;
    const c3 = chartFor(ctx, t, key3);
    if (c3 && inRange(c3, ctx.hero.cards)) {
        return { role: 'defend', intent: 'three_bet', family: fam, chartKey: key3, inChart: true };
    }
    const cC = chartFor(ctx, t, keyC);
    if (cC && inRange(cC, ctx.hero.cards)) {
        return { role: 'defend', intent: 'call', family: fam, chartKey: keyC, inChart: true };
    }
    // No `_vs_` chart for this defender/opener pairing (e.g. hero UTG1 facing a UTG open) - that node is
    // 3bet-or-fold: 3bet the premium stack-off, fold the rest.
    if (!c3 && !cC) {
        return inRange(PREMIUM_STACKOFF, ctx.hero.cards)
            ? { role: 'defend', intent: 'three_bet', family: fam, chartKey: 'premium_v_open', inChart: true }
            : fold('defend', fam);
    }
    return fold('defend', fam);
};
const resolveVs3bet = (ctx, t) => {
    const fam = familyId(t, ctx.tableSize);
    const hb = positionBucket(ctx.hero.position);
    const key4 = `${hb}_open_v3b_4bet`;
    const keyC = `${hb}_open_v3b_call`;
    const c4 = chartFor(ctx, t, key4);
    if (c4 && inRange(c4, ctx.hero.cards)) {
        return { role: 'vs3bet', intent: 'four_bet', family: fam, chartKey: key4, inChart: true };
    }
    const cC = chartFor(ctx, t, keyC);
    if (cC && inRange(cC, ctx.hero.cards)) {
        return { role: 'vs3bet', intent: 'call', family: fam, chartKey: keyC, inChart: true };
    }
    return fold('vs3bet', fam);
};
const resolveVs4bet = (ctx, t) => {
    const fam = familyId(t, ctx.tableSize);
    return inRange(PREMIUM_STACKOFF, ctx.hero.cards)
        ? { role: 'vs4bet', intent: 'jam', family: fam, chartKey: 'premium_stackoff', inChart: true }
        : fold('vs4bet', fam);
};
const resolveVsJam = (ctx, t) => {
    const fam = familyId(t, ctx.tableSize);
    return inRange(PREMIUM_STACKOFF, ctx.hero.cards)
        ? { role: 'vsjam', intent: 'call', family: fam, chartKey: 'premium_calloff', inChart: true }
        : fold('vsjam', fam);
};
/** The pure chart-membership baseline for the spot in the base table-type family. */
const preflopBaseline = (ctx) => {
    switch (ctx.facing.role) {
        case 'open':
            return resolveOpen(ctx, ctx.tableType);
        case 'defend':
            return resolveDefend(ctx, ctx.tableType);
        case 'vs3bet':
            return resolveVs3bet(ctx, ctx.tableType);
        case 'vs4bet':
            return resolveVs4bet(ctx, ctx.tableType);
        case 'vsjam':
            return resolveVsJam(ctx, ctx.tableType);
    }
};
// ── exploit biases (chart-bounded widen / tighten) ────────────────────────────────────────────────
/** Apply the soft value/call biases the exploit rules carried, using the honest per-spot primitives.
 *  Returns a possibly-shifted resolution (family/chartKey reflect the effective decision). */
const applyPreflopBiases = (base, ctx, biases) => {
    let out = base;
    // value widen ── 3bet wider for value / steal-iso wider.
    if (biases.valueThreshold === 'thinner') {
        if (out.role === 'defend' && out.intent === 'call' && !isPocketPair(ctx.hero.cards)) {
            out = { ...out, intent: 'three_bet' }; // 3bet your (non-pair) flatting range for value vs a wide opener.
        }
        else if (out.role === 'open' && out.intent === 'fold') {
            const looser = shiftTableType(ctx.tableType, 'looser');
            if (looser !== ctx.tableType) {
                const r = resolveOpen(ctx, looser);
                if (r.inChart)
                    out = r; // open a hand the looser table would (steal/iso wider).
            }
        }
    }
    // value tighten ── 3bet only the premium core / open the tighter core.
    if (biases.valueThreshold === 'stricter') {
        if (out.role === 'defend' && out.intent === 'three_bet' && out.chartKey.endsWith('_3bet')) {
            // demote a THIN 3bet to a flat, but keep the premiums: a hand still in the tighter family's 3bet
            // chart stays a 3bet; one that drops out becomes a call (or fold).
            const tighter = shiftTableType(ctx.tableType, 'tighter');
            if (tighter !== ctx.tableType) {
                const c3 = chartFor(ctx, tighter, out.chartKey);
                if (c3 !== undefined && !inRange(c3, ctx.hero.cards))
                    out = demoteDefendToCall(ctx);
            }
        }
        else if (out.role === 'open' && out.intent === 'open') {
            const tighter = shiftTableType(ctx.tableType, 'tighter');
            if (tighter !== ctx.tableType) {
                const r = resolveOpen(ctx, tighter);
                if (!r.inChart)
                    out = fold('open', out.family); // open only the tighter core.
            }
        }
    }
    // call widen ── defend more flats (vs a bully who auto-raises).
    if (biases.callThreshold === 'wider' && out.role === 'defend' && out.intent === 'fold') {
        const looser = shiftTableType(ctx.tableType, 'looser');
        if (looser !== ctx.tableType) {
            const b = ctx.facing.openerPosition ? positionBucket(ctx.facing.openerPosition) : 'BTN';
            const keyC = `${ctx.hero.position}_vs_${b}_call`;
            const cC = chartFor(ctx, looser, keyC);
            if (cC && inRange(cC, ctx.hero.cards)) {
                out = { role: 'defend', intent: 'call', family: familyId(looser, ctx.tableSize), chartKey: keyC, inChart: true };
            }
        }
    }
    // call tighten ── release the marginal flats (vs a nit whose open is strong).
    if (biases.callThreshold === 'tighter' && out.role === 'defend' && out.intent === 'call') {
        out = fold('defend', out.family);
    }
    return out;
};
/** demote a defend 3bet down to its flat membership (or fold) - used by value-tighten. */
const demoteDefendToCall = (ctx) => {
    const fam = familyId(ctx.tableType, ctx.tableSize);
    const b = ctx.facing.openerPosition ? positionBucket(ctx.facing.openerPosition) : 'BTN';
    const keyC = `${ctx.hero.position}_vs_${b}_call`;
    const cC = chartFor(ctx, ctx.tableType, keyC);
    return cC && inRange(cC, ctx.hero.cards)
        ? { role: 'defend', intent: 'call', family: fam, chartKey: keyC, inChart: true }
        : fold('defend', fam);
};
// ── action.set / forbid, mechanical mapping, sizing ──────────────────────────────────────────────
const raiseIntentFor = (role) => role === 'open' ? 'open' : role === 'defend' ? 'three_bet' : role === 'vs3bet' ? 'four_bet' : 'jam';
const mechanicalAction = (intent) => {
    switch (intent) {
        case 'fold':
            return 'fold';
        case 'limp':
        case 'call':
            return 'call';
        case 'open':
        case 'three_bet':
        case 'four_bet':
            return 'raise';
        case 'jam':
            return 'jam';
    }
};
/** Per-role de-escalation ladder (most → least aggressive) when the intended action is forbidden. */
const ROLE_LADDER = {
    open: ['open', 'limp', 'fold'],
    defend: ['three_bet', 'call', 'fold'],
    vs3bet: ['four_bet', 'call', 'fold'],
    vs4bet: ['jam', 'call', 'fold'],
    vsjam: ['call', 'fold'],
};
/** Honor an explicit `action.set` (never manufacturing a raise from a fold-hand) then de-escalate past
 *  any forbidden action down the role ladder. */
const applySetAndForbid = (intent, role, eff) => {
    let out = intent;
    const set = eff.setAction?.value;
    if (set) {
        if (set === 'fold') {
            out = 'fold';
        }
        else if (out !== 'fold' && !eff.forbidden.has(set)) {
            // redirect a hand hero is already playing; junk (fold) is never turned into a raise.
            if (set === 'raise')
                out = raiseIntentFor(role);
            else if (set === 'jam')
                out = 'jam';
            else if (set === 'call')
                out = role === 'open' ? 'limp' : 'call';
        }
    }
    if (eff.forbidden.has(mechanicalAction(out))) {
        const ladder = ROLE_LADDER[role];
        const start = Math.max(0, ladder.indexOf(out));
        out = ladder.slice(start).find((i) => !eff.forbidden.has(mechanicalAction(i))) ?? 'fold';
    }
    return out;
};
/** Whether the collected sizing effects push the raise bigger (an iso/3bet size-up floor at ≥ pot). */
const wantsBigger = (eff) => {
    const potOrd = 5; // SIZING_LADDER index of 'pot' (min,third,half,twoThirds,threeQuarters,pot,...)
    const ord = (s) => s ? ['min', 'third', 'half', 'twoThirds', 'threeQuarters', 'pot', 'overbet', 'jam'].indexOf(s.value) : -1;
    return ord(eff.floor) >= potOrd || ord(eff.setSizing) >= potOrd;
};
const round1 = (n) => Math.round(n * 10) / 10;
/** The concrete preflop sizing target for a playing intent (fold has none). Precise EV of the size is
 *  PF2; this emits the honest convention (openSizeBB / iso 3bb+1/limper / 3x-4x) as a bb target. */
const preflopSizing = (intent, ctx, eff) => {
    const bigger = wantsBigger(eff);
    const openBase = ctx.tableType === 'baseline' ? 2.5 : 3.0; // live opens a touch bigger
    const limpers = ctx.facing.limpers ?? 0;
    const faced = ctx.facing.toCallBB ?? openBase;
    switch (intent) {
        case 'fold':
            return undefined;
        case 'limp':
            return { toBB: 1, label: 'open-limp (leak)' };
        case 'call':
            return { toBB: round1(faced), label: `call ${round1(faced)}bb` };
        case 'open': {
            if (limpers > 0) {
                const perLimper = bigger ? 2 : 1;
                const to = 3 + perLimper * limpers;
                return { toBB: round1(to), label: `iso 3bb+${perLimper}/limper` };
            }
            const to = bigger ? openBase + 1 : openBase;
            return { toBB: round1(to), label: `${round1(to)}bb open` };
        }
        case 'three_bet': {
            const mult = ctx.positionClass === 'IP' ? 3 : 4;
            const to = (mult + (bigger || (ctx.facing.callers ?? 0) > 0 ? 1 : 0)) * faced;
            return { toBB: round1(to), label: `${mult}x 3bet` };
        }
        case 'four_bet':
            return { toBB: round1(2.3 * faced), label: '2.3x 4bet' };
        case 'jam':
            return { toBB: round1(ctx.facing.effectiveStackBB), label: 'all-in' };
    }
};
// ── strategy-context bridge (reuse ruleMatches) ───────────────────────────────────────────────────
const facingEventFor = (role) => role === 'defend' ? 'raise' : role === 'vs3bet' ? '3bet' : role === 'vs4bet' ? '4bet' : role === 'vsjam' ? 'jam' : 'check';
/** raises-so-far → potType (matches scenario/spot.ts potTypeOf): open node = 0 raises = 'limped'. */
const potTypeFor = (role) => role === 'open' ? 'limped' : role === 'defend' ? 'srp' : role === 'vs3bet' ? '3bp' : '4bp';
/** Effective-stack → sprBucket, the "sprBucket proxies stack depth preflop" convention the shipped ICM
 *  short-stack rules key on (rules.v1.json ICM_SHORT_JAM_DONT_CALL note). */
const stackDepthBucket = (bb) => bb <= 12 ? 'under1' : bb <= 25 ? '1to3' : bb <= 45 ? '3to6' : bb <= 80 ? '6to13' : 'over13';
/** Build the StrategyContext preflop rule matching needs. Postflop-only fields carry inert placeholders
 *  (textureClass 'dry', handClass 'air') - preflop rules key on facing/potType/sprBucket/icm, never on
 *  those, exactly as yardstick/run.ts:38 documents. */
const toStrategyContext = (ctx) => ({
    street: 'preflop',
    facing: facingEventFor(ctx.facing.role),
    positionClass: ctx.positionClass,
    potType: ctx.facing.potType ?? potTypeFor(ctx.facing.role),
    playersInHand: ctx.playersInHand,
    spr: ctx.facing.effectiveStackBB,
    sprBucket: stackDepthBucket(ctx.facing.effectiveStackBB),
    textureClass: 'dry',
    price: { pot: ctx.potBB ?? 0, toCall: ctx.facing.toCallBB ?? 0 },
    hero: { handClass: 'air', drawFlags: [], equity: 0 },
    villain: ctx.villain,
    ...(ctx.icm ? { icm: ctx.icm } : {}),
    rules: ctx.rules,
});
// ── presentation ─────────────────────────────────────────────────────────────────────────────────
const alternativesFor = (role, intent) => {
    switch (role) {
        case 'open':
            return intent === 'open'
                ? [{ intent: 'limp', note: 'open-limp - allowed, but graded as a leak' }, { intent: 'fold' }]
                : intent === 'limp'
                    ? [{ intent: 'open', note: 'raising is the stronger line' }, { intent: 'fold' }]
                    : [{ intent: 'open', note: 'out of the opening chart' }];
        case 'defend':
            return intent === 'three_bet'
                ? [{ intent: 'call' }, { intent: 'fold' }]
                : intent === 'call'
                    ? [{ intent: 'three_bet' }, { intent: 'fold' }]
                    : [{ intent: 'call' }, { intent: 'three_bet' }];
        case 'vs3bet':
            return intent === 'four_bet'
                ? [{ intent: 'call' }, { intent: 'fold' }]
                : intent === 'call'
                    ? [{ intent: 'four_bet' }, { intent: 'fold' }]
                    : [{ intent: 'call' }];
        case 'vs4bet':
            return intent === 'jam' ? [{ intent: 'fold' }] : [{ intent: 'jam', note: 'the premium stack-off' }];
        case 'vsjam':
            return intent === 'call' ? [{ intent: 'fold' }] : [{ intent: 'call', note: 'a premium call-off' }];
    }
};
/** One honest sentence naming what the recommended line does and why, IN THE PRODUCT'S VOICE - this string
 *  is rendered verbatim to the player, so it speaks to him ("your hand", "this seat") and never leaks the
 *  engine's internal register ("hero", "combo", "chart region"). It always ends in a period: callers
 *  concatenate it with other sentences. A read-driven deviation names the baseline it left, because the
 *  deviation IS the teaching (ENGINE_DESIGN §6.2). */
const describeTarget = (res, baselineIntent, fired) => {
    const deviated = res.intent !== baselineIntent && fired;
    const because = deviated ? ` The read moves you off the default ${labelFor(baselineIntent)} here.` : '';
    switch (res.intent) {
        case 'open':
            return `Your hand is in the opening range from this seat, so raise.${because}`;
        case 'three_bet':
            return `Your hand is strong enough to re-raise the open for value and pressure.${because}`;
        case 'four_bet':
            return `Your hand plays as a 4-bet against this 3-bet.${because}`;
        case 'call':
            return `Your hand is worth a flat call at this price and position.${because}`;
        case 'limp':
            return `Limping in is playable here, but raising is the stronger line.${because}`;
        case 'jam':
            return `Your hand is strong enough to get the whole stack in.${because}`;
        case 'fold':
            return `Your hand is not in the range that plays from this seat, so fold.${because}`;
    }
};
const labelFor = (intent) => intent === 'three_bet' ? '3-bet' : intent === 'four_bet' ? '4-bet' : intent === 'open' ? 'open-raise' : intent;
/**
 * Did this matched rule's effect actually SHAPE the answer the engine gave?
 *
 * Matching is not the same as mattering. A rule matches on the SPOT (a limped pot, a caller in between)
 * without knowing hero's cards, and the resolver can then decline its effect - applySetAndForbid refuses to
 * turn a hand the chart folds into a raise, and a widen bias only widens a hand that was already close. When
 * that happens the rule's principle argues for a line the engine did not take, and rendering it as the
 * explanation is how a debrief ends up saying "fold" and "size up over the limpers" in one breath.
 *
 * A rule stays in `firedRules` either way - that it matched is real provenance, and the yardstick and the
 * dimension attribution assert on exactly that. This only answers whether its PROSE explains the answer.
 */
const ruleShapedAnswer = (r, finalAction, finalIntent) => {
    const e = r.effect;
    // An explicit `action.set` shaped the answer only if the engine actually took that action.
    if (e.action?.set)
        return e.action.set === finalAction;
    // Everything below is advice about putting money in. When the answer is fold there is no bet to size and
    // no thin value to take, so a sizing floor or a widen bias explains nothing about the fold.
    if (finalIntent === 'fold') {
        if (e.sizingIntent)
            return false;
        if (e.valueThreshold === 'thinner' || e.callThreshold === 'wider')
            return false;
    }
    return true;
};
// ── PF5: short-stack / tournament push/fold routing ───────────────────────────────────────────────
//
// The reconciliation (docs/PF5_PREFLOP_RECONCILIATION.md): ONE hero preflop entry (recommendPreflop)
// routes by effective stack. At push/fold depth (≤ 20bb, the DT chart domain) with a DT chart injected,
// the two nodes the DT corpus AUTHORS resolve off the Nash charts:
//   • an OPEN node (folded to hero, first-in)  → jam-or-fold   (resolveJam)
//   • a vsjam node (hero faces an all-in shove) → call-or-fold  (resolveCallOff)
// A short-stack DEFEND / vs3bet / vs4bet (facing a non-all-in raise) is a re-shove-or-fold spot the DT
// corpus does NOT author a chart for, so it is NOT re-modeled here (fabricating a Nash re-shove range
// would violate North Star §4.1 - never invent strategy off a named chart); it stays on the cash chart
// path, which at short depth already degenerates to an all-in-sized 3bet/stack-off. That gap is a DT data
// follow-up, flagged in the reconciliation doc - not an engine one.
//
// The jam/call membership is chipEV-Nash (the authored chart is the ground truth). ICM enters as CONTEXT:
// ctx.icm.pressure is carried onto the recommendation's pushFold provenance for the coach/grader; the
// ICM-EV overfold adjustment (a chipEV-good call the bubble turns into a fold) is ST1's equity-gated
// callAllInDecision, NOT re-derived in this pure resolver.
/** Build a push/fold PreflopRecommendation. The Nash cell IS the baseline (no chart-vs-rule deviation
 *  preflop for push/fold), so baselineIntent === finalIntent and firedRules is empty - the tournament
 *  exploit-rule overlay is ST1's recommendTournament, layered by the caller, not here. */
const buildPushFoldRec = (ctx, role, intent, action, prov, inChart) => {
    const eff = round1(ctx.facing.effectiveStackBB);
    const depth = Math.round(ctx.facing.effectiveStackBB);
    const pressureClause = prov.icmPressure && prov.icmPressure !== 'none'
        ? ` ICM pressure here is ${prov.icmPressure}, so lean toward the disciplined line.`
        : '';
    let sizing;
    let targetStatement;
    let alternatives;
    if (intent === 'jam') {
        sizing = { toBB: eff, label: 'all-in (Nash jam)' };
        targetStatement = `At ${depth}bb this is a jam-or-fold spot, and your hand is in the shoving range, so get it in.${pressureClause}`;
        alternatives = [{ intent: 'fold' }];
    }
    else if (intent === 'call') {
        sizing = { toBB: eff, label: 'call the shove (all-in)' };
        targetStatement = `Your hand is strong enough to call off the shove at ${depth}bb, so call.${pressureClause}`;
        alternatives = [{ intent: 'fold' }];
    }
    else {
        // fold
        targetStatement =
            role === 'open'
                ? `At ${depth}bb this is a jam-or-fold spot, and your hand is below the shoving range, so fold.${pressureClause}`
                : `Your hand is below the calling range for this shove at ${depth}bb, so fold.${pressureClause}`;
        alternatives = [
            role === 'open'
                ? { intent: 'jam', note: 'the Nash jamming range' }
                : { intent: 'call', note: 'the Nash call-off range' },
        ];
    }
    const rec = {
        role,
        baselineIntent: intent,
        finalIntent: intent,
        action,
        chartKey: `pushfold:${role === 'open' ? 'jam' : 'call'}:${prov.position}@${prov.band}`,
        family: prov.chartId,
        inChart,
        firedRules: [],
        alternatives,
        targetStatement,
        pushFold: prov,
    };
    if (sizing)
        rec.sizing = sizing;
    return rec;
};
/** Resolve an open or vsjam node off the DT Nash push/fold charts (PF5). Pre-gated by recommendPreflop
 *  on `ctx.pushFold` + push/fold depth + role, so `ctx.pushFold` is guaranteed present here. */
const recommendPushFold = (ctx) => {
    const chart = ctx.pushFold.chart;
    const eff = ctx.facing.effectiveStackBB;
    const hole = ctx.hero.cards;
    const heroPos = pushFoldPositionOf(ctx.hero.position);
    const pressure = ctx.icm?.pressure;
    if (ctx.facing.role === 'vsjam') {
        const shoverPos = ctx.pushFold.shoverPosition
            ? pushFoldPositionOf(ctx.pushFold.shoverPosition)
            : 'BTN'; // default: a late (steal) shove, the modal short-stack all-in.
        const shoverClass = shoverClassOf(shoverPos);
        const dec = resolveCallOff(chart, heroPos, eff, shoverClass, hole);
        const intent = dec.action === 'call' ? 'call' : 'fold';
        return buildPushFoldRec(ctx, 'vsjam', intent, dec.action, {
            chartId: chart.id,
            position: dec.position,
            band: dec.band,
            rangePct: dec.rangePct,
            shoverClass,
            ...(pressure ? { icmPressure: pressure } : {}),
        }, dec.inRange);
    }
    // role === 'open': jam-or-fold (folded to hero, first in).
    const dec = resolveJam(chart, heroPos, eff, hole);
    const intent = dec.action === 'jam' ? 'jam' : 'fold';
    return buildPushFoldRec(ctx, 'open', intent, dec.action, {
        chartId: chart.id,
        position: dec.position,
        band: dec.band,
        rangePct: dec.rangePct,
        ...(pressure ? { icmPressure: pressure } : {}),
    }, dec.inRange);
};
/** True iff this decision routes to the DT push/fold path: a chart is injected, the effective stack is at
 *  push/fold depth (owned by tournament/staging), and the node is one the DT corpus authors (open/vsjam). */
const routesToPushFold = (ctx) => ctx.pushFold !== undefined &&
    isPushFoldDepth(ctx.facing.effectiveStackBB) &&
    (ctx.facing.role === 'open' || ctx.facing.role === 'vsjam');
/**
 * recommendPreflop - resolve hero's exact combo against the shipped charts + preflop exploit rules and
 * emit a labeled preflop intent + sizing target. Pure function of `ctx`; same context ⇒ same answer.
 *
 * PF5: this is the ONE hero preflop entry, routed by effective stack. Short-stack / tournament push/fold
 * spots (≤ 20bb with a DT chart injected, at an open/vsjam node) resolve off the DT Nash charts; every
 * other spot - all cash/deep-stack play - resolves off the shipped position × table-type charts below,
 * unchanged. See docs/PF5_PREFLOP_RECONCILIATION.md.
 */
export const recommendPreflop = (ctx) => {
    if (routesToPushFold(ctx))
        return recommendPushFold(ctx);
    const base = preflopBaseline(ctx);
    // Only rules that explicitly declare `street:['preflop']` are eligible - this excludes the
    // street-agnostic postflop rules (which gate on facing 'bet-large'/heroHandClass floors the preflop
    // placeholders don't satisfy anyway) so a mis-scoped rule can never fire preflop.
    const preflopRules = ctx.rules.filter((r) => r.when.street?.includes('preflop'));
    const sctx = toStrategyContext(ctx);
    const matched = orderRules(preflopRules.filter((r) => ruleMatches(r, sctx)));
    const eff = collectEffects(matched);
    const adjusted = applyPreflopBiases(base, ctx, eff.biases);
    const finalIntent = applySetAndForbid(adjusted.intent, adjusted.role, eff);
    // finalIntent may differ from adjusted.intent (a set/de-escalation); keep the resolution's provenance
    // but reflect the final action's chart-membership honesty (a forced fold is out of chart).
    const inChart = finalIntent === adjusted.intent ? adjusted.inChart : finalIntent !== 'fold' && adjusted.inChart;
    const chartKey = finalIntent === 'fold' ? 'none' : adjusted.chartKey;
    const sizing = preflopSizing(finalIntent, ctx, eff);
    const resForDisplay = { ...adjusted, intent: finalIntent, chartKey, inChart };
    const finalAction = mechanicalAction(finalIntent);
    const firedRules = matched.map((r) => {
        const fired = toFiredRule(r);
        return ruleShapedAnswer(r, finalAction, finalIntent) ? fired : { ...fired, suppressed: true };
    });
    const rec = {
        role: base.role,
        baselineIntent: base.intent,
        finalIntent,
        action: finalAction,
        chartKey,
        family: adjusted.family,
        inChart,
        firedRules,
        alternatives: alternativesFor(base.role, finalIntent),
        targetStatement: describeTarget(resForDisplay, base.intent, matched.length > 0),
    };
    if (sizing)
        rec.sizing = sizing;
    if (finalIntent === 'limp')
        rec.isLeak = true;
    return rec;
};
// ── deriveHeroPreflopSpot: the HandState → facing-line mirror of deriveVillainSpotKey ─────────────
// (kept minimal for PF1 - PF3 wires the played-preflop handoff; this proves the mirror resolves the
// same spot vocabulary hero-side.)
export { deriveHeroPreflopSpot } from './heroSpot';
//# sourceMappingURL=preflop.js.map